import {commitUpdateIssueMilestoneMutation} from '@github-ui/item-picker/commitUpdateIssueMilestoneMutation'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import {MilestonePicker} from '@github-ui/item-picker-milestone-tsq'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {ActionList} from '@primer/react'
import {useCallback, useMemo} from 'react'
import {graphql, useFragment, useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../constants/labels'
import {ReadonlySectionHeader} from '../ReadonlySectionHeader'
import {Section} from '../Section'
import {SectionHeader} from '../SectionHeader'
import type {MilestonesSectionFragment$key} from './__generated__/MilestonesSectionFragment.graphql'
import type {MilestonesSectionMilestone$key} from './__generated__/MilestonesSectionMilestone.graphql'
import {Milestone} from './Milestone'

export const ReadonlyMilestonesSectionHeader = () => <ReadonlySectionHeader title={LABELS.sectionTitles.milestones} />

type MilestonesSectionProps = {
  sectionHeader: React.ReactElement
  milestone: MilestonePickerMilestone | null
  className?: string
}
export const MilestonesSection = ({sectionHeader, milestone, className}: MilestonesSectionProps) => (
  <Section
    id={`sidebar-milestones-section`}
    className={className}
    sectionHeader={sectionHeader}
    emptyText={milestone ? undefined : LABELS.emptySections.milestones}
  >
    <ActionList variant="full">
      <Milestone milestone={milestone} />
    </ActionList>
  </Section>
)

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const milestonesSectionMilestone = graphql`
  fragment MilestonesSectionMilestone on Issue {
    milestone {
      id
      # eslint-disable-next-line relay/unused-fields
      title
      # eslint-disable-next-line relay/unused-fields
      closed
      # eslint-disable-next-line relay/unused-fields
      dueOn
      # eslint-disable-next-line relay/unused-fields
      progressPercentage
      # eslint-disable-next-line relay/unused-fields
      url
      # eslint-disable-next-line relay/unused-fields
      closedAt
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const MilestonesSectionFragment = graphql`
  fragment MilestonesSectionFragment on Issue {
    repository {
      name
      owner {
        login
      }
    }
    id
    ...MilestonesSectionMilestone
    viewerCanSetMilestone
  }
`

export type EditIssueMilestonesSectionProps = {
  issue: MilestonesSectionFragment$key
  canCreateMilestone?: boolean
  onIssueUpdate?: () => void
  insideSidePanel?: boolean
  readonly: boolean
}
export function EditIssueMilestonesSection({
  issue,
  canCreateMilestone = false,
  onIssueUpdate,
  insideSidePanel,
  readonly,
}: EditIssueMilestonesSectionProps) {
  const data = useFragment(MilestonesSectionFragment, issue)
  const {
    repository: {
      owner: {login: owner},
      name: repo,
    },
    id: issueId,
    viewerCanSetMilestone,
  } = data
  const {milestone} = useFragment<MilestonesSectionMilestone$key>(milestonesSectionMilestone, data)
  const {addToast} = useToastContext()
  const environment = useRelayEnvironment()

  const onSelectionChanged = useCallback(
    (selectedMilestones: MilestonePickerMilestone[]) => {
      commitUpdateIssueMilestoneMutation({
        environment,
        input: {issueId, milestone: selectedMilestones.length > 0 ? selectedMilestones.at(-1)! : null},
        onError: () =>
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateMilestone,
          }),
        onCompleted: onIssueUpdate,
      })
    },
    [addToast, environment, issueId, onIssueUpdate],
  )

  const sectionHeader = useMemo(() => {
    const readonlyMilestone = readonly || !viewerCanSetMilestone
    if (readonlyMilestone) {
      return <ReadonlyMilestonesSectionHeader />
    }

    return (
      <MilestonePicker
        repo={repo}
        owner={owner}
        onSelectionChange={onSelectionChanged}
        anchorElement={(anchorProps, ref) => (
          <SectionHeader title={LABELS.sectionTitles.milestones} buttonProps={anchorProps} ref={ref} />
        )}
        readonly={readonlyMilestone}
        canCreateMilestone={canCreateMilestone}
        activeMilestone={milestone ?? null}
        insidePortal={insideSidePanel}
        showMilestoneDescription
        showNoMatchItem
      />
    )
  }, [readonly, viewerCanSetMilestone, canCreateMilestone, repo, owner, onSelectionChanged, milestone, insideSidePanel])

  return <MilestonesSection sectionHeader={sectionHeader} milestone={milestone ?? null} />
}
